import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET } from '@env';

export interface CloudinaryUploadResult {
  url: string;
  publicId: string;
  bytes: number;
  format: string;
  resourceType: string;
  originalFilename: string;
}

export interface CloudinaryUploadOptions {
  folder?: string;
  filename?: string;
  onProgress?: (pct: number) => void;
}

export function cloudinaryConfigured(): boolean {
  return Boolean(CLOUDINARY_CLOUD_NAME && CLOUDINARY_UPLOAD_PRESET);
}

/**
 * Sube un archivo (Blob/File) a Cloudinary usando un upload preset unsigned.
 * Funciona para imágenes y PDFs (el resource_type "auto" lo detecta solo).
 */
export async function subirACloudinary(
  file: Blob,
  opts: CloudinaryUploadOptions = {},
): Promise<CloudinaryUploadResult> {
  if (!cloudinaryConfigured()) {
    throw new Error(
      'Cloudinary no está configurado (faltan CLOUDINARY_CLOUD_NAME o CLOUDINARY_UPLOAD_PRESET en .env).',
    );
  }

  const endpoint = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`;
  const form = new FormData();
  form.append('file', file);
  form.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  if (opts.folder) form.append('folder', opts.folder);

  // Si se requiere progreso, usamos XHR para poder reportar.
  if (opts.onProgress) {
    return new Promise<CloudinaryUploadResult>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', endpoint);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && opts.onProgress) {
          opts.onProgress((e.loaded / e.total) * 100);
        }
      };
      xhr.onload = () => {
        try {
          if (xhr.status >= 200 && xhr.status < 300) {
            const data = JSON.parse(xhr.responseText);
            resolve(parseResponse(data));
          } else {
            reject(new Error(`Cloudinary upload falló (${xhr.status}): ${xhr.responseText}`));
          }
        } catch (e) {
          reject(e);
        }
      };
      xhr.onerror = () => reject(new Error('Cloudinary upload: error de red'));
      xhr.send(form);
    });
  }

  const res = await fetch(endpoint, { method: 'POST', body: form });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Cloudinary upload falló (${res.status}): ${text}`);
  }
  const data = await res.json();
  return parseResponse(data);
}

function parseResponse(data: any): CloudinaryUploadResult {
  return {
    url: data.secure_url,
    publicId: data.public_id,
    bytes: data.bytes ?? 0,
    format: data.format ?? '',
    resourceType: data.resource_type ?? 'auto',
    originalFilename: data.original_filename ?? '',
  };
}
